import {
  PlaygroundLocalMsg,
  usePlaygroundLocalMsg,
} from "@faire/prototype-playground/platform/localization";
import { Button } from "@faire/slate/components/button";
import { Checkbox } from "@faire/slate/components/checkbox";
import { Trash } from "@phosphor-icons/react";
import { useState } from "react";

import { RetailerMobileNav } from "./RetailerMobileNav";
import {
  RETURN_REQUEST_BRAND_NAME,
  RETURN_REQUEST_RETURN_BY,
  getReturnRequestItemCount,
  type ReturnRequestProduct,
} from "./returnRequestData";

type ReturnRequestReviewScreenProps = {
  products: ReturnRequestProduct[];
  onRemoveProduct: (productId: string) => void;
  onEditResolution: () => void;
  onAddMore: () => void;
  onBack: () => void;
  onSubmit: () => void;
};

const ReturnRequestReviewProductCard = ({
  product,
  onRemove,
}: {
  product: ReturnRequestProduct;
  onRemove: () => void;
}) => {
  const removeLabel = usePlaygroundLocalMsg("Remove {name}", {
    name: product.name,
  });

  return (
    <div className="bg-fs-surface-tertiary border-fs-action-border-active flex gap-(--slate-spacing-element-medium) rounded-[4px] border p-(--slate-spacing-element-large)">
      <div className="bg-fs-surface-tertiary size-[72px] shrink-0 overflow-hidden rounded-[2px]">
        <img
          src={product.imageUrl}
          alt=""
          className="size-full object-cover"
        />
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-[2px]">
        <div className="flex items-start justify-between gap-(--slate-dimensions-2)">
          <span className="type-fs-paragraph-medium text-fs-message-text-critical">
            {product.reviewIssueLabel}
          </span>
          <button
            type="button"
            aria-label={removeLabel}
            className="text-fs-text-primary flex size-4 shrink-0 items-center justify-center"
            onClick={onRemove}
          >
            <Trash size={16} weight="regular" />
          </button>
        </div>
        <span className="type-fs-paragraph-medium text-fs-text-primary">
          {product.name}
        </span>
        <span className="type-fs-paragraph text-fs-text-primary">
          {product.color}
        </span>
        <span className="type-fs-paragraph text-fs-text-primary">
          {product.size}
        </span>
      </div>
    </div>
  );
};

export const ReturnRequestReviewScreen = ({
  products,
  onRemoveProduct,
  onEditResolution,
  onAddMore,
  onBack,
  onSubmit,
}: ReturnRequestReviewScreenProps) => {
  const [hasAcknowledgedReturn, setHasAcknowledgedReturn] = useState(false);
  const itemCount = getReturnRequestItemCount(products);
  const submitLabel = usePlaygroundLocalMsg("Submit");
  const addMoreLabel = usePlaygroundLocalMsg("Add more");
  const editLabel = usePlaygroundLocalMsg("Edit");
  const acknowledgeLabel = usePlaygroundLocalMsg(
    "I understand that returning items is required to receive a refund."
  );

  return (
    <div className="bg-fs-surface-primary flex min-h-full flex-col">
      <RetailerMobileNav progressFraction={0.85} onBack={onBack} />
      <div className="flex flex-1 flex-col px-(--slate-spacing-section-medium) pt-(--slate-spacing-section-medium) pb-(--slate-spacing-section-large)">
        <h1 className="type-fs-page-heading text-fs-text-primary mb-(--slate-spacing-section-small)">
          <PlaygroundLocalMsg defaultMessage="Review & confirm" />
        </h1>

        <div className="flex flex-col gap-(--slate-spacing-section-small)">
          <div className="flex flex-col gap-(--slate-dimensions-2)">
            <span className="type-fs-paragraph-medium text-fs-text-primary">
              <PlaygroundLocalMsg defaultMessage="Resolution" />
            </span>
            <div className="flex items-center gap-(--slate-dimensions-2)">
              <span className="type-fs-paragraph text-fs-text-primary">
                <PlaygroundLocalMsg defaultMessage="Replace items" />
              </span>
              <button
                type="button"
                className="type-fs-paragraph text-fs-text-primary underline"
                onClick={onEditResolution}
              >
                {editLabel}
              </button>
            </div>
            <p className="type-fs-label text-fs-text-primary">
              <PlaygroundLocalMsg
                defaultMessage="If {brand} approves the replacements, a new order will be charged separately — shipping is free."
                values={{ brand: RETURN_REQUEST_BRAND_NAME }}
              />
            </p>
          </div>

          <div className="flex flex-col gap-(--slate-dimensions-2)">
            <span className="type-fs-paragraph-medium text-fs-text-primary">
              <PlaygroundLocalMsg
                defaultMessage="Return {count} items and receive a refund"
                values={{ count: itemCount }}
              />
            </span>
            <p className="type-fs-label text-fs-text-primary">
              <PlaygroundLocalMsg
                defaultMessage="Return the following items to {brand} by {date} to receive a refund. A pre-paid label will be provided once you submit this report."
                values={{
                  brand: RETURN_REQUEST_BRAND_NAME,
                  date: RETURN_REQUEST_RETURN_BY,
                }}
              />
            </p>
            <div className="mt-(--slate-spacing-element-medium) flex flex-col gap-(--slate-dimensions-2)">
              {products.map((product) => (
                <ReturnRequestReviewProductCard
                  key={product.id}
                  product={product}
                  onRemove={() => onRemoveProduct(product.id)}
                />
              ))}
              <Button variant="plain" size="small" onClick={onAddMore}>
                {addMoreLabel}
              </Button>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-fs-surface-primary sticky bottom-0 flex flex-col gap-(--slate-spacing-element-large) px-(--slate-spacing-section-medium) pt-(--slate-dimensions-2) pb-(--slate-spacing-section-medium)">
        <div className="border-fs-action-border-subdued rounded-[4px] border p-(--slate-spacing-element-large)">
          <Checkbox
            checked={hasAcknowledgedReturn}
            label={acknowledgeLabel}
            onChange={() =>
              setHasAcknowledgedReturn((current) => !current)
            }
          />
        </div>
        <Button
          variant="primary"
          fullWidth
          disabled={!hasAcknowledgedReturn || products.length === 0}
          onClick={onSubmit}
        >
          {submitLabel}
        </Button>
      </div>
    </div>
  );
};
